import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../config/endpoints';
import { formatMoney } from '../utils/money';

/**
 * A player's own record.
 *
 * Counts settled rounds only: a round cancelled by a server restart is refunded,
 * so counting it as "played" would make an outage look like a losing streak.
 * Everything here comes from our own database — no analytics SDK is involved.
 */

interface Stats {
    roundsPlayed: number; busts: number; cashouts: number; cashoutRate: number;
    wagered: string; paidOut: string; net: string; avgBet: string;
    avgCashoutMultiplier: number | null; autoCashoutShare: number;
    biggestWin: string; biggestLoss: string;
    daysPlayed: number; currentStreakDays: number; longestStreakDays: number;
    sessions: number; avgSessionMinutes: number;
    firstPlayedAt: string | null; lastPlayedAt: string | null;
}
interface Day { day: string; rounds: number; wagered: string; net: string; sessions: number }

const percent = (value: number) => `${Math.round(value * 100)}%`;

/** Title row shared by every state, so Close is always there. */
const Heading: React.FC<{ onClose?: () => void }> = ({ onClose }) => (
    <div className="panel-heading">
        <div><p className="section-label">Your play</p><h2>Your record</h2></div>
        {onClose && <button className="auth-switch settings-close" onClick={onClose}>Close</button>}
    </div>
);

const MyStats: React.FC<{ onClose?: () => void }> = ({ onClose }) => {
    const { token } = useAuth();
    const [stats, setStats] = useState<Stats | null>(null);
    const [days, setDays] = useState<Day[]>([]);
    const [error, setError] = useState('');

    // The server groups by the player's own calendar, not the server's, so
    // "days played" and streaks match what the player sees on their phone.
    const tz = useMemo(() => -new Date().getTimezoneOffset(), []);

    useEffect(() => {
        if (!token) return;
        let cancelled = false;
        const headers = { Authorization: `Bearer ${token}` };
        Promise.all([
            fetch(`${API_URL}/api/me/stats?tz=${tz}`, { headers }).then(r => (r.ok ? r.json() : Promise.reject(new Error('stats')))),
            fetch(`${API_URL}/api/me/activity?days=30&tz=${tz}`, { headers }).then(r => (r.ok ? r.json() : Promise.reject(new Error('activity')))),
        ])
            .then(([statsBody, activityBody]) => {
                if (cancelled) return;
                setStats(statsBody);
                setDays(activityBody.days ?? []);
            })
            .catch(() => { if (!cancelled) setError('Your statistics are unavailable right now.'); });
        return () => { cancelled = true; };
    }, [token, tz]);

    if (error) return <div className="panel my-stats"><Heading onClose={onClose} /><p className="auth-error" role="alert">{error}</p></div>;
    if (!stats) return <div className="panel my-stats"><Heading onClose={onClose} /><p className="auth-subtitle">Loading your record…</p></div>;

    if (stats.roundsPlayed === 0) {
        return (
            <div className="panel my-stats">
                <Heading onClose={onClose} />
                <p className="auth-subtitle">Play a round and your record starts here.</p>
            </div>
        );
    }

    const busiest = Math.max(1, ...days.map(day => day.rounds));
    const net = Number(stats.net);

    return (
        <div className="panel my-stats">
            <Heading onClose={onClose} />

            <dl className="stat-grid">
                <div className="stat-headline"><dt>Rounds</dt><dd>{stats.roundsPlayed}</dd></div>
                <div><dt>Cashed out</dt><dd>{percent(stats.cashoutRate)}</dd></div>
                <div><dt>Wagered</dt><dd>{formatMoney(Number(stats.wagered))}</dd></div>
                <div className="stat-headline">
                    <dt>Net</dt>
                    {/* Sign carries the meaning; colour only reinforces it. */}
                    <dd className={net >= 0 ? 'stat-up' : 'stat-down'}>
                        {net >= 0 ? '+' : '−'}{formatMoney(Math.abs(net))}
                    </dd>
                </div>
                <div><dt>Average wager</dt><dd>{formatMoney(Number(stats.avgBet))}</dd></div>
                <div><dt>Average exit</dt><dd>{stats.avgCashoutMultiplier ? `${stats.avgCashoutMultiplier.toFixed(2)}×` : '—'}</dd></div>
                <div><dt>Best win</dt><dd>{formatMoney(Number(stats.biggestWin))}</dd></div>
                <div><dt>Automatic exits</dt><dd>{percent(stats.autoCashoutShare)}</dd></div>
                <div><dt>Days played</dt><dd>{stats.daysPlayed}</dd></div>
                <div><dt>Current streak</dt><dd>{stats.currentStreakDays} {stats.currentStreakDays === 1 ? 'day' : 'days'}</dd></div>
                <div><dt>Longest streak</dt><dd>{stats.longestStreakDays} {stats.longestStreakDays === 1 ? 'day' : 'days'}</dd></div>
                <div><dt>Average session</dt><dd>{stats.avgSessionMinutes ? `${stats.avgSessionMinutes} min` : '—'}</dd></div>
            </dl>

            <h3>Last 30 days</h3>
            <ol className="activity-bars" aria-label="Rounds played per day over the last 30 days">
                {days.map(day => (
                    <li key={day.day} title={`${day.day}: ${day.rounds} rounds`}>
                        <span
                            className="activity-bar"
                            style={{ height: `${Math.round((day.rounds / busiest) * 100)}%` }}
                            aria-hidden="true"
                        />
                        <span className="sr-only">{day.day}: {day.rounds} rounds</span>
                    </li>
                ))}
            </ol>
            <div className="activity-axis" aria-hidden="true"><span>30 days ago</span><span>Today</span></div>
        </div>
    );
};

export default MyStats;
